import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { DataTable, EmptyState, PageHeader, StatusChip, useNotify, type DataTableColumn, type StatusTone } from '@routes-plan/ui';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import StandardModal from '../../../components/modales/StandardModal';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { companyLocale } from '../../../utils/format/currency';
import { FilterBar, OfficeRouteFields, SelectField } from '../components/ProcessFilters';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface MobileSyncAuditListRow {
  id: number;
  createdAt: string;
  eventType?: string | null;
  syncEntity?: string | null;
  status?: string | null;
  message?: string | null;
  userId?: number | null;
  routeId?: number | null;
  visitOrderId?: number | null;
  loanId?: number | null;
  localRef?: string | null;
  appVersion?: string | null;
  deviceInstallationId?: string | null;
  responseStatus?: number | null;
  officeName?: string | null;
  routeName?: string | null;
  userName?: string | null;
  username?: string | null;
}

interface MobileSyncAuditDetail extends MobileSyncAuditListRow {
  requestPayload?: string | null;
  responseBody?: string | null;
  metaJson?: string | null;
  officeId?: number | null;
}

const formatDateTime = (value?: string) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(companyLocale());
};

const prettifyMaybeJson = (raw?: string | null) => {
  if (raw === null || raw === undefined || raw === '') return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch (_e) {
    return raw;
  }
};

// Tono del tema por estado (claro y oscuro)
const statusTone: Record<string, StatusTone> = {
  success: 'success',
  error: 'danger',
  warning: 'warning',
  warn: 'warning',
  info: 'info',
  partial: 'neutral',
};

const SyncAuditList: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const notify = useNotify();
  const showAlert = (message: string, severity: 'success' | 'info' | 'warning' | 'error' = 'info') => notify[severity](message);
  useEffect(() => {
    setPageTitle('syncAudit.title');
  }, []);
  const { canRead } = useResourcePermissions('/processes/sync-audit');

  const [rows, setRows] = useState<MobileSyncAuditListRow[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [routeId, setRouteId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<MobileSyncAuditDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadRows = async (officeFilter = officeId, routeFilter = routeId, status = statusFilter) => {
    if (!canRead) {
      setRows([]);
      return;
    }
    showLoading();
    try {
      const { data } = await axios.get('sync-audits', {
        params: {
          officeId: officeFilter || undefined,
          routeId: routeFilter || undefined,
          status: status || undefined,
          limit: 350,
        },
      });

      setRows(data?.data ?? []);
    } catch (error: any) {
      console.error('Error cargando auditoría sync:', error);
      showAlert(
        error?.response?.data?.message || t('syncAudit.loadError'),
        'error',
      );
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      showLoading();
      try {
        const { data } = await axios.get('offices');
        setOffices(data?.data ?? []);
      } catch (error) {
        showAlert(t('syncAudit.officesLoadError'), 'error');
      } finally {
        hideLoading();
      }
    };

    bootstrap();
    if (canRead) {
      loadRows('', '', '');
    }
  }, [canRead]);

  useEffect(() => {
    if (!officeId) {
      setRoutes([]);
      setRouteId('');
      return;
    }

    const fetchRoutes = async () => {
      showLoading();
      try {
        const { data } = await axios.get(`offices/${officeId}/routes`);
        setRoutes(data ?? []);
      } catch (error) {
        setRoutes([]);
        showAlert(t('syncAudit.routesLoadError'), 'warning');
      } finally {
        hideLoading();
      }
    };

    fetchRoutes();
  }, [officeId]);

  const handleSearch = async () => {
    if (!canRead) {
      showAlert(t('syncAudit.noPermissionQueryAlert'), 'warning');
      return;
    }
    await loadRows();
  };

  const openDetail = async (id: number) => {
    if (!canRead) return;
    setDetailLoading(true);
    setDetail(null);
    setDetailOpen(true);
    try {
      const { data } = await axios.get(`sync-audits/${id}`);
      setDetail(data?.data ?? null);
    } catch (error: any) {
      showAlert(error?.response?.data?.message || t('syncAudit.detailLoadError'), 'error');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const tableRows = rows.map((r) => ({
    ...r,
    id: r.id,
    createdLabel: formatDateTime(r.createdAt),
    statusKey: (r.status || 'info').toLowerCase(),
    statusLabel: r.status || '—',
    userDisplay: [r.userName?.trim(), r.username].filter(Boolean).join(' · ') || '—',
    visitOrderId: r.visitOrderId ?? '—',
    loanId: r.loanId ?? '—',
    responseStatus: r.responseStatus ?? '—',
  }));

  type Row = (typeof tableRows)[number];
  const columns: DataTableColumn<Row>[] = [
    { id: 'createdAt', header: t('common.date'), value: (r) => r.createdAt, render: (r) => r.createdLabel, sortable: true },
    {
      id: 'status',
      header: t('common.status'),
      value: (r) => r.statusLabel,
      render: (r) => <StatusChip label={r.statusLabel} tone={statusTone[r.statusKey] ?? 'info'} />,
      sortable: true,
    },
    { id: 'eventType', header: t('syncAudit.colEvent'), value: (r) => r.eventType ?? '', sortable: true },
    { id: 'syncEntity', header: t('syncAudit.colEntity'), value: (r) => r.syncEntity ?? '', sortable: true, hideBelow: 'sm' },
    { id: 'routeName', header: t('common.route'), value: (r) => r.routeName ?? '', sortable: true, hideBelow: 'sm' },
    { id: 'officeName', header: t('common.office'), value: (r) => r.officeName ?? '', sortable: true, hideBelow: 'md' },
    { id: 'userDisplay', header: t('common.user'), value: (r) => r.userDisplay, hideBelow: 'md' },
    { id: 'message', header: t('syncAudit.colMessage'), value: (r) => r.message ?? '', wrap: true },
    { id: 'id', header: t('syncAudit.colId'), value: (r) => r.id, align: 'right', hideBelow: 'md' },
    { id: 'visitOrderId', header: t('syncAudit.colRouteId'), value: (r) => r.visitOrderId, align: 'right', hideBelow: 'md' },
    { id: 'loanId', header: t('syncAudit.colLoan'), value: (r) => r.loanId, align: 'right', hideBelow: 'md' },
    { id: 'localRef', header: t('syncAudit.colLocalRef'), value: (r) => r.localRef ?? '', hideBelow: 'md' },
    { id: 'appVersion', header: t('syncAudit.colApp'), value: (r) => r.appVersion ?? '', hideBelow: 'md' },
    { id: 'deviceInstallationId', header: t('syncAudit.colInstallation'), value: (r) => r.deviceInstallationId ?? '', hideBelow: 'md' },
    { id: 'responseStatus', header: t('syncAudit.colHttp'), value: (r) => r.responseStatus, align: 'right', hideBelow: 'md' },
  ];

  const statusOptions = [
    { id: 'error', name: t('syncAudit.statusError') },
    { id: 'success', name: t('syncAudit.statusSuccess') },
    { id: 'warning', name: t('syncAudit.statusWarning') },
    { id: 'info', name: t('syncAudit.statusInfo') },
  ];

  const preSx = {
    m: 0,
    p: 1.5,
    bgcolor: 'action.hover',
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    maxHeight: 240,
    overflow: 'auto',
    fontSize: 12,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  };

  return (
    <HeaderResponsive>
      <PageHeader title={t('syncAudit.title')} description={t('syncAudit.subtitle')} helpAction={<ScreenTourButton />} />

      <FilterBar>
        <OfficeRouteFields
          idPrefix="sync-audit"
          tourPrefix="sync-audit"
          allowAll
          offices={offices}
          routes={routes}
          officeId={officeId}
          routeId={routeId}
          onOfficeChange={setOfficeId}
          onRouteChange={setRouteId}
        />
        <SelectField
          id="sync-audit-status"
          tourId="sync-audit-status"
          label={t('common.status')}
          value={statusFilter}
          options={statusOptions}
          onChange={setStatusFilter}
          emptyLabel={t('common.all')}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 56 }}>
          <PermissionButton
            allowed={canRead}
            deniedTooltip={t('syncAudit.noPermissionQuery')}
            fullWidth
            size="large"
            variant="contained"
            startIcon={<HistoryOutlinedIcon />}
            onClick={handleSearch}
            data-tour="sync-audit-query"
            spanSx={{ width: '100%' }}
          >
            {t('syncAudit.query')}
          </PermissionButton>
        </Box>
      </FilterBar>

      <DataTable<Row>
        rows={tableRows}
        columns={columns}
        getRowId={(r) => r.id}
        searchable
        exportFileName={t('syncAudit.exportFileName')}
        initialSort={{ columnId: 'createdAt', direction: 'desc' }}
        ariaLabel={t('syncAudit.title')}
        tourId="sync-audit"
        onRowClick={canRead ? (r) => void openDetail(Number(r.id)) : undefined}
        rowActions={(r) => (
          <Tooltip title={t('syncAudit.viewDetail')}>
            <span>
              <IconButton
                size="small"
                aria-label={`${t('syncAudit.viewDetail')} ${r.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  void openDetail(Number(r.id));
                }}
                disabled={!canRead}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        emptyState={<EmptyState dense icon={<SyncOutlinedIcon />} title={t('syncAudit.empty')} />}
      />

      <StandardModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={t('syncAudit.detailTitle', { id: detail?.id ?? '' })}
        maxWidth={900}
        loading={detailLoading}
        actions={
          <>
            <span />
            <Button variant="contained" onClick={() => setDetailOpen(false)}>{t('common.close')}</Button>
          </>
        }
      >
        {detailLoading ? null : detail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <Box
              component="dl"
              sx={{
                m: 0,
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'max-content minmax(0, 1fr)' },
                columnGap: 2,
                rowGap: 0.75,
                '& dt': { typography: 'body2', color: 'text.secondary' },
                '& dd': { m: 0, typography: 'body2', overflowWrap: 'anywhere' },
              }}
            >
              <dt>{t('syncAudit.detailDate')}</dt>
              <dd>{formatDateTime(detail.createdAt)}</dd>
              <dt>{t('common.status')}</dt>
              <dd>
                <StatusChip label={detail.status || '—'} tone={statusTone[String(detail.status || 'info').toLowerCase()] ?? 'info'} />
              </dd>
              <dt>{t('syncAudit.detailOfficeRoute')}</dt>
              <dd>
                {detail.officeName ?? '—'} / {detail.routeName ?? '—'}
              </dd>
              <dt>{t('syncAudit.detailUser')}</dt>
              <dd>{[detail.userName?.trim(), detail.username].filter(Boolean).join(' · ') || '—'}</dd>
              <dt>{t('syncAudit.detailMessage')}</dt>
              <dd>{detail.message || '—'}</dd>
            </Box>
            <Typography variant="subtitle2">{t('syncAudit.requestPayload')}</Typography>
            <Box component="pre" sx={preSx}>
              {prettifyMaybeJson(detail.requestPayload) || '—'}
            </Box>
            <Typography variant="subtitle2">{t('syncAudit.responseBody')}</Typography>
            <Box component="pre" sx={preSx}>
              {prettifyMaybeJson(detail.responseBody) || '—'}
            </Box>
            <Typography variant="subtitle2">{t('syncAudit.meta')}</Typography>
            <Box component="pre" sx={{ ...preSx, maxHeight: 160 }}>
              {prettifyMaybeJson(detail.metaJson) || '—'}
            </Box>
          </Box>
        ) : null}
      </StandardModal>
    </HeaderResponsive>
  );
};

export default SyncAuditList;
